/**
 * ArtifactSelectionList - Reusable row-based artifact selection UI
 *
 * Used in:
 * - Artifact Generation Panel (select artifacts to generate)
 * - Export Modal (select artifacts to export)
 *
 * Features:
 * - Collapsible type-based sections (Skills, Attributes, etc.)
 * - Tri-state checkboxes (none, some, all)
 * - Show first 6, "Show all" to expand
 * - Condensed metadata display
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, File, Database, Plug, GitBranch, FileText, Unplug, CheckSquare, Square, Filter, FilterX } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  Artifact,
  SkillArtifact,
  SubskillArtifact,
  SubflowArtifact,
  AttributeArtifact,
  IntegrationArtifact,
  FacetArtifact
} from '@/types/artifactDesign';

// Artifact type configuration (matches Explorer)
const ARTIFACT_TYPE_CONFIG = {
  skill: {
    icon: File,
    label: 'Skills',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10'
  },
  subskill: {
    icon: FileText,
    label: 'Subskills',
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10'
  },
  subflow: {
    icon: GitBranch,
    label: 'Subflows',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10'
  },
  integration: {
    icon: Plug,
    label: 'Integrations',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10'
  },
  attribute: {
    icon: Database,
    label: 'Attributes',
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10'
  },
  facet: {
    icon: Unplug,
    label: 'Facets',
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10'
  }
};

type ArtifactType = 'skill' | 'subskill' | 'subflow' | 'attribute' | 'integration' | 'facet';

export interface ArtifactGroup {
  type: ArtifactType;
  artifacts: Artifact[];
}

export interface ArtifactSelectionListProps {
  /** Groups of artifacts by type */
  groups: ArtifactGroup[];
  /** Called when selection changes */
  onSelectionChange: (artifactId: string, artifactType: ArtifactType, selected: boolean) => void;
  /** Called when all artifacts of a type are toggled */
  onTypeToggle: (artifactType: ArtifactType, selected: boolean) => void;
  /** Called when "Select All" / "Deselect All" is clicked */
  onSelectAll?: (selected: boolean) => void;
  /** Show only selected artifacts (filter toggle) */
  showSelectedOnly?: boolean;
  /** Called when show selected filter is toggled */
  onToggleShowSelected?: (showSelected: boolean) => void;
  /** Mode: 'generation' or 'export' */
  mode?: 'generation' | 'export';
}

export const ArtifactSelectionList: React.FC<ArtifactSelectionListProps> = ({
  groups,
  onSelectionChange,
  onTypeToggle,
  onSelectAll,
  showSelectedOnly = false,
  onToggleShowSelected,
  mode = 'generation'
}) => {
  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Set<ArtifactType>>(
    new Set(['skill', 'subskill', 'subflow', 'attribute', 'integration', 'facet'])
  );

  // Track which sections show all items (vs first 6)
  const [showAllSections, setShowAllSections] = useState<Set<ArtifactType>>(new Set());

  // Track warning dialog
  const [showDeselectWarning, setShowDeselectWarning] = useState(false);

  const toggleSection = (type: ArtifactType) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedSections(newExpanded);
  };

  const toggleShowAll = (type: ArtifactType) => {
    const newShowAll = new Set(showAllSections);
    if (newShowAll.has(type)) {
      newShowAll.delete(type);
    } else {
      newShowAll.add(type);
    }
    setShowAllSections(newShowAll);
  };

  // Calculate total artifacts and selected count
  const totalArtifacts = groups.reduce((sum, g) => sum + g.artifacts.length, 0);
  const totalSelected = groups.reduce((sum, g) => sum + g.artifacts.filter(a => a.selected).length, 0);
  const allSelected = totalArtifacts > 0 && totalSelected === totalArtifacts;

  // Filter groups to only show those with selected artifacts (when filter is enabled)
  const filteredGroups = showSelectedOnly
    ? groups.map(group => ({
        ...group,
        artifacts: group.artifacts.filter(a => a.selected)
      })).filter(group => group.artifacts.length > 0)
    : groups;

  const hiddenCount = totalArtifacts - totalSelected;

  const handleSelectAllClick = () => {
    if (!onSelectAll) return;

    // Show warning if changing selection (either selecting or deselecting)
    // Only skip warning if nothing is selected (initial state)
    if (totalSelected > 0) {
      setShowDeselectWarning(true);
    } else {
      // No warning needed when nothing is selected
      onSelectAll(true);
    }
  };

  const handleConfirmChange = () => {
    if (onSelectAll) {
      // Toggle: if all selected, deselect; otherwise select all
      onSelectAll(!allSelected);
    }
    setShowDeselectWarning(false);
  };

  return (
    <div className="w-full">
      {/* Select All / Deselect All Button */}
      {onSelectAll && totalArtifacts > 0 && (
        <div className="border-b border-border bg-muted/30 px-3 py-2 flex items-center justify-between">
          <button
            onClick={handleSelectAllClick}
            className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
          >
            {allSelected ? (
              <>
                <Square className="w-4 h-4" />
                <span>Deselect All</span>
              </>
            ) : (
              <>
                <CheckSquare className="w-4 h-4" />
                <span>Select All</span>
              </>
            )}
          </button>
          <span className="text-xs text-muted-foreground">
            {totalSelected} of {totalArtifacts} selected
          </span>
        </div>
      )}

      {/* Warning Dialog */}
      {showDeselectWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background border border-border rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-3">
              {allSelected ? 'Deselect All Artifacts?' : 'Select All Artifacts?'}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {allSelected
                ? 'Are you sure you want to deselect all artifacts? You will need to manually reselect them if you change your mind.'
                : `Are you sure you want to select all artifacts? This will change your current selection of ${totalSelected} artifact${totalSelected === 1 ? '' : 's'}.`
              }
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeselectWarning(false)}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmChange}
                className="px-4 py-2 text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 rounded transition-colors"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show Selected Filter Toggle */}
      {onToggleShowSelected && totalArtifacts > 0 && (
        <div className="border-b border-border bg-background px-3 py-2 flex items-center justify-between">
          <button
            onClick={() => onToggleShowSelected(!showSelectedOnly)}
            className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors"
          >
            {showSelectedOnly ? (
              <>
                <FilterX className="w-4 h-4" />
                <span>Show All Artifacts</span>
              </>
            ) : (
              <>
                <Filter className="w-4 h-4" />
                <span>Show Selected Only</span>
              </>
            )}
          </button>
          {showSelectedOnly && hiddenCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {hiddenCount} {hiddenCount === 1 ? 'artifact' : 'artifacts'} hidden
            </span>
          )}
        </div>
      )}

      {filteredGroups.map((group) => {
        const config = ARTIFACT_TYPE_CONFIG[group.type];
        const Icon = config.icon;
        const isExpanded = expandedSections.has(group.type);
        const showAll = showAllSections.has(group.type);

        const totalCount = group.artifacts.length;
        const selectedCount = group.artifacts.filter(a => a.selected).length;
        const displayedArtifacts = showAll ? group.artifacts : group.artifacts.slice(0, 6);
        const hasMore = !showAll && totalCount > 6;

        // Calculate checkbox state (unchecked, indeterminate, checked)
        const checkboxState = selectedCount === 0 ? 'none' : selectedCount === totalCount ? 'all' : 'some';

        return (
          <div key={group.type} className="border-b border-border">
            {/* Section Header */}
            <div
              className={cn(
                "flex items-center gap-2 py-2 px-3 hover:bg-muted/50 cursor-pointer transition-colors",
                config.bgColor
              )}
              onClick={() => toggleSection(group.type)}
            >
              {/* Expand/Collapse Chevron */}
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}

              {/* Type Checkbox */}
              <input
                type="checkbox"
                checked={checkboxState === 'all'}
                ref={(el) => {
                  if (el) el.indeterminate = checkboxState === 'some';
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  onTypeToggle(group.type, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0"
              />

              {/* Type Icon */}
              <Icon className={cn("w-4 h-4 flex-shrink-0", config.color)} />

              {/* Type Label + Count */}
              <span className="text-sm font-medium flex-shrink-0">
                {config.label} ({totalCount})
              </span>

              {/* Selection Status */}
              {selectedCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  • {selectedCount} of {totalCount} selected
                </span>
              )}
            </div>

            {/* Artifacts List (when expanded) */}
            {isExpanded && (
              <div className="bg-background">
                {displayedArtifacts.map((artifact) => (
                  <ArtifactRow
                    key={artifact.id}
                    artifact={artifact}
                    type={group.type}
                    onToggle={(selected) => onSelectionChange(artifact.id, group.type, selected)}
                  />
                ))}

                {/* Empty State */}
                {totalCount === 0 && (
                  <div className="py-4 px-12 text-sm text-muted-foreground italic">
                    No {config.label.toLowerCase()} detected
                  </div>
                )}

                {/* Show All Button */}
                {hasMore && (
                  <button
                    onClick={() => toggleShowAll(group.type)}
                    className="w-full py-2 px-12 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-left"
                  >
                    Show all ({totalCount})
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Individual Artifact Row Component
interface ArtifactRowProps {
  artifact: Artifact;
  type: ArtifactType;
  onToggle: (selected: boolean) => void;
}

const ArtifactRow: React.FC<ArtifactRowProps> = ({ artifact, type, onToggle }) => {
  return (
    <div
      className="flex items-center gap-3 py-2 px-12 hover:bg-muted/30 transition-colors border-t border-border/50"
      onClick={() => onToggle(!artifact.selected)}
    >
      {/* Checkbox */}
      <input
        type="checkbox"
        checked={artifact.selected}
        onChange={(e) => onToggle(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        className="flex-shrink-0"
      />

      {/* Artifact Name */}
      <span className="font-medium text-sm flex-shrink-0 min-w-[200px]">
        {artifact.name}
      </span>

      {/* Identifier */}
      <span className="text-xs font-mono text-muted-foreground flex-shrink-0 min-w-[150px]">
        {artifact.identifier}
      </span>

      {/* Type-specific Metadata */}
      <span className="text-xs text-muted-foreground truncate flex-1">
        {getMetadataDisplay(artifact, type)}
      </span>

      {/* Status Badge */}
      {artifact.status && (
        <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
          {artifact.status}
        </span>
      )}
    </div>
  );
};

// Helper to get condensed metadata display
function getMetadataDisplay(artifact: Artifact, type: ArtifactType): string {
  switch (type) {
    case 'attribute': {
      const attr = artifact as AttributeArtifact;
      return `${attr.dataType} • ${attr.scope}`;
    }
    case 'integration': {
      const int = artifact as IntegrationArtifact;
      const protocol = int.integrationType || int.connector?.protocol || 'Integration';
      const opCount = int.operations?.length || 0;
      return opCount > 0 ? `${protocol} • ${opCount} op${opCount === 1 ? '' : 's'}` : protocol;
    }
    case 'facet': {
      const facet = artifact as FacetArtifact;
      return facet.facetType || '';
    }
    case 'skill':
    case 'subskill':
    case 'subflow': {
      return artifact.description || '';
    }
    default:
      return '';
  }
}
